import { PendingIssueListView } from '@euler/app/flows/common/components/PendingIssueListView';
import { Aside } from '@euler/components/Aside';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  isMobileLayout,
  useIsMobileLayout,
} from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { PendingIssueInfoWithDetail } from '@euler/model/PendingIssueInfoWithDetail';
import {
  PendingIssue,
  PendingIssueDetail,
  PendingIssueMedia,
} from '@euler/model/report';
import { useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { noop } from 'rxjs';

export const PendingIssueWithDetailsPage = wrapNavigatorScreen(
  ({
    pendingIssueWithDetails,
  }: {
    pendingIssueWithDetails: PendingIssueInfoWithDetail;
  }) => {
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const isMobile = useIsMobileLayout();

    const pendingIssues = useMemo<PendingIssue[]>(() => {
      const { issue, details, medias } = pendingIssueWithDetails;
      return [
        {
          name: issue.name,
          type: issue.type,
          defectiveLevel: issue.defectiveLevel,
          issueId: issue.issueId,
          details: details.map<PendingIssueDetail>(detail => ({
            detailId: detail.detailId,
            title: detail.title,
            isCustomIssue: detail.isCustomIssue,
            abnormalLevel: detail.abnormalLevel,
            severityLevel: detail.severityLevel,
            inspectionType: detail.inspectionType,
            siteId: detail.siteId ?? undefined,
            siteName: detail.siteName ?? undefined,
            siteCode: detail.siteCode ?? undefined,
            itemName: detail.itemName ?? undefined,
            itemId: detail.itemId ?? undefined,
            customIssueId: detail.customIssueId ?? undefined,
            authorId: detail.authorId ?? undefined,
            authorName: detail.authorName ?? undefined,
            inspectionResult: detail.inspectionResult ?? undefined,
            defectiveClass: detail.defectiveClass ?? undefined,
            remark: detail.remark ?? undefined,
            medias: medias
              .filter(x => x.detailId === detail.detailId)
              .map<PendingIssueMedia>(media => ({
                cid: media.cid,
                type: media.type,
                url: media.url,
                coverUrl: media.coverUrl ?? undefined,
                remark: media.remark ?? undefined,
              })),
          })),
        },
      ];
    }, [pendingIssueWithDetails]);

    if (isMobile) {
      return (
        <PendingIssueListView
          pendingIssues={pendingIssues}
          loading={false}
          refreshing={false}
          error={undefined}
          onRefresh={noop}
          contentContainerStyle={{
            paddingBottom: bottom + rpx(44),
          }}
        />
      );
    }

    return (
      <Aside dismissible>
        <LayoutProviderView
          css={`
            flex: 1;
          `}
        >
          <PendingIssueListView
            pendingIssues={pendingIssues}
            loading={false}
            refreshing={false}
            error={undefined}
            onRefresh={noop}
            contentContainerStyle={{
              paddingBottom: bottom + rpx(44),
            }}
          />
        </LayoutProviderView>
      </Aside>
    );
  },
  adaptiveWrapperOptions<any>(({ route }) => ({
    title: route.params.pendingIssueWithDetails.issue.name,
    headerStatusBarHeight: undefined,
    headerShown: isMobileLayout(),
  })),
);
